/**
 * Le module **Support et litiges** — file de réclamations, dossier, et les
 * quatre gestes de traitement (`EX-SUP-001`, `EX-SUP-003`, `EX-BO-004`).
 *
 * Maquette : artboard `b_support` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * `test/app/support-maquette.test.tsx` **relit** cette maquette et la confronte
 * au livré, plutôt que d'en recopier les valeurs ici (`D-34`).
 *
 * ## Ce que ce chantier referme
 *
 * `SupportApi` servait deux routes, éprouvées, appelées par aucun écran —
 * pendant que le socle exécutait déjà **six** actions d'écriture pour de vrai.
 * C'est la forme la plus coûteuse de la famille `D-25` / `D-30` / `D-35` : tout
 * était là, et personne ne pouvait s'en servir.
 *
 * ## L'échéance est SERVIE, elle ne se recalcule pas
 *
 * `slaDueAt` est un **instant**, écrit par le serveur à partir du
 * `sla_minutes` de la catégorie. L'écran le compare à maintenant pour dire
 * « dépassé » — il ne recalcule aucune échéance. Une échéance recalculée par le
 * navigateur sur son horloge à lui serait fausse dès que les deux divergent, et
 * un SLA faux est pire qu'un SLA absent : il se lit comme une mesure.
 *
 * ## Une note interne N'EST PAS une réponse
 *
 * C'est le point de cet écran qu'il ne faut pas manquer. Le fil porte les deux,
 * et `internal` les sépare. Les confondre ferait croire à un agent qu'il a
 * répondu au client alors qu'il a pris une note — ou, pire, qu'il annote alors
 * qu'il écrit au client, dont les mots partiront tels quels. L'écran les
 * distingue donc **par le mot**, et pas seulement par une teinte.
 *
 * ## Les quatre gestes sont ceux du serveur, et le serveur les défend
 *
 * `ASSIGN`, `REPLY`, `STATUS`, `CLOSE`. Un geste inconnu rend
 * `SUP_GESTURE_UNKNOWN` en énumérant ceux qui existent — l'écran n'en invente
 * aucun, et un contrôle relit les constantes de `SupportDesk.java` pour que la
 * liste ne vieillisse pas en silence.
 *
 * ## Ce que le dessin porte et que rien ne sert
 *
 * Quatre indicateurs — tickets ouverts, hors SLA, délai médian, satisfaction —
 * et quatre filtres. Le **délai médian** et la **satisfaction** n'ont aucune
 * route : `support.satisfaction_survey` existe en base, mais rien ne l'agrège
 * et aucune route ne la sert. Les afficher fabriquerait deux chiffres que
 * personne ne calcule, sur l'écran qui mesure la qualité du service. Les deux
 * premiers, eux, se comptent **sur la file reçue** — c'est le seul calcul que
 * cet écran s'autorise, et il porte sur ce qu'il a sous les yeux.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueShell, QueueDetailSection, QueueFact } from '../shell/QueueShell.js';
import type { QueueColumn, QueueDecision, QueueEntry } from '../shell/QueueShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import { fetchTicketFile, fetchTicketQueue, handleTicket } from '../http/support.js';
import type {
  SupportGesture,
  SupportTicket,
  SupportTicketFile,
} from '../http/support.js';

/** Les colonnes de la file, dans l'ordre du dessin. */
const COLONNES: readonly QueueColumn[] = [
  { id: 'reference', labelKey: 'support.columns.ticket', emphasis: 'strong' },
  { id: 'category', labelKey: 'support.columns.category' },
  { id: 'subject', labelKey: 'support.columns.subject' },
  { id: 'openedAt', labelKey: 'support.columns.opened', emphasis: 'muted' },
  { id: 'priority', labelKey: 'support.columns.priority', emphasis: 'numeric' },
  { id: 'sla', labelKey: 'support.columns.sla' },
];

/**
 * Les quatre gestes, offerts sur le dossier ouvert.
 *
 * **Aucun ne dispose du dossier** (`advances: false`), et c'est le contraire de
 * la revue d'identité. Là-bas, une décision clôt le dossier et la file avance ;
 * ici, affecter puis répondre puis clore sont trois gestes sur le **même**
 * ticket. Refermer le panneau après une affectation obligerait à rouvrir — donc
 * à écrire une seconde consultation au journal — pour répondre.
 */
const GESTES: readonly QueueDecision[] = [
  { id: 'ASSIGN', labelKey: 'support.gestures.assign', shortcut: 'A', advances: false },
  { id: 'REPLY', labelKey: 'support.gestures.reply', shortcut: 'R', advances: false },
  { id: 'STATUS', labelKey: 'support.gestures.status', shortcut: 'S', advances: false },
  { id: 'CLOSE', labelKey: 'support.gestures.close', shortcut: 'C', advances: false },
];

/** Le module Support et litiges. */
export function SupportScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();

  const [file, setFile] = useState<readonly SupportTicket[] | null>(null);
  const [erreurFile, setErreurFile] = useState<string | null>(null);
  const [dossier, setDossier] = useState<SupportTicketFile | null>(null);
  const [erreurDossier, setErreurDossier] = useState<string | null>(null);
  const [geste, setGeste] = useState<SupportGesture | null>(null);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);

  const relireLaFile = useCallback(async (): Promise<void> => {
    try {
      const tickets = await fetchTicketQueue(client);
      setFile(tickets);
      setErreurFile(null);
    } catch (echec) {
      // La file lue est effacée avec l'échec : la garder ferait dépiler des
      // réclamations que le serveur vient de refuser de servir.
      setFile(null);
      setErreurFile(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client]);

  useEffect(() => {
    void relireLaFile();
  }, [relireLaFile]);

  const ouvrir = useCallback(
    (entree: QueueEntry): void => {
      setDossier(null);
      setErreurDossier(null);
      setGeste(null);
      setErreurGeste(null);
      void (async () => {
        try {
          // **Un seul appel pour cinq choses.** Le serveur rend le ticket, le
          // client, le fil, l'historique et les opérations ensemble : cinq
          // appels écriraient cinq consultations au journal pour une ouverture.
          setDossier(await fetchTicketFile(client, entree.id));
        } catch (echec) {
          setErreurDossier(echec instanceof ApiError ? echec.messageKey : 'errors.server');
        }
      })();
    },
    [client],
  );

  /** Les champs que chaque geste demande — et ils ne se ressemblent pas. */
  const champsDuGeste: readonly FormFieldDescriptor[] = useMemo(() => {
    const motif: FormFieldDescriptor = {
      name: 'reason',
      labelKey: 'support.fields.reason',
      control: 'text',
      placeholderKey: 'support.fields.reasonPlaceholder',
      required: true,
    };
    if (geste === 'REPLY') {
      return [
        {
          name: 'body',
          labelKey: 'support.fields.body',
          control: 'text',
          placeholderKey: 'support.fields.bodyPlaceholder',
          required: true,
        },
        {
          name: 'internal',
          labelKey: 'support.fields.internal',
          control: 'select',
          placeholderKey: 'support.fields.internalPlaceholder',
          options: [
            { value: 'false', label: t('support.fields.internalNo') },
            { value: 'true', label: t('support.fields.internalYes') },
          ],
          required: true,
        },
        motif,
      ];
    }
    if (geste === 'STATUS') {
      return [
        {
          name: 'status',
          labelKey: 'support.fields.status',
          control: 'text',
          placeholderKey: 'support.fields.statusPlaceholder',
          required: true,
        },
        motif,
      ];
    }
    if (geste === 'CLOSE') {
      return [
        {
          name: 'resolution',
          labelKey: 'support.fields.resolution',
          control: 'text',
          placeholderKey: 'support.fields.resolutionPlaceholder',
          required: true,
        },
        motif,
      ];
    }
    return [motif];
  }, [geste, t]);

  const soumettreLeGeste = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      if (dossier === null || geste === null) return;
      const { reason, ...charge } = valeurs;
      try {
        const issue = await handleTicket(client, {
          ticketId: dossier.ticket.id,
          gesture: geste,
          reason: reason ?? '',
          payload: charge,
        });
        setErreurGeste(null);
        setGeste(null);
        // Le DÉTAIL vient du serveur : c'est lui qui dit ce qui a été fait, et
        // l'écran ne le paraphrase pas. Une phrase composée ici annoncerait ce
        // que l'écran croit avoir demandé, pas ce que le socle a exécuté.
        setAvis(issue.detail);
        // Le dossier ET la file sont relus : un geste change l'état du ticket,
        // son fil, et sa place dans la file. Ne relire que l'un des deux
        // laisserait l'autre montrer un état révolu.
        setDossier(await fetchTicketFile(client, dossier.ticket.id));
        await relireLaFile();
      } catch (echec) {
        setErreurGeste(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client, dossier, geste, relireLaFile],
  );

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (file ?? []).map((ticket) => ({
        id: ticket.id,
        reference: ticket.reference,
        values: [
          ticket.reference,
          ticket.categoryCode,
          ticket.subject,
          ticket.openedAt,
          String(ticket.priority),
          ticket.slaDueAt ?? '',
        ],
        cells: [
          ticket.reference,
          ticket.categoryCode,
          ticket.subject,
          ticket.openedAt,
          String(ticket.priority),
          <SlaBadge key="sla" ticket={ticket} />,
        ],
      })),
    [file],
  );

  const horsSla = useMemo(
    () => (file ?? []).filter((ticket) => estDepasse(ticket)).length,
    [file],
  );

  return (
    <section aria-label={t('support.landmark')} className="support">
      <p className="support__journal-notice">{t('support.journalNotice')}</p>

      {avis === null ? null : (
        <p className="support__avis" role="status">
          {avis}
        </p>
      )}

      {erreurFile === null ? null : (
        <p className="support__notice" role="alert">
          {t(erreurFile)}
        </p>
      )}

      {file === null ? (
        erreurFile === null ? (
          <p className="support__idle">{t('support.loading')}</p>
        ) : null
      ) : (
        <>
          {/* **Les deux indicateurs qui se comptent, et eux seuls.** Ils
              portent sur la file REÇUE — ce que l'écran a sous les yeux — et
              non sur un total que personne ne sert. Le délai médian et la
              satisfaction du dessin n'ont aucune route : voir l'en-tête. */}
          <div className="support__stats">
            <p className="support__stat" data-testid="support-stat-open">
              <span className="support__stat-label">{t('support.stats.open')}</span>
              <span className="support__stat-value">{file.length}</span>
            </p>
            <p className="support__stat" data-testid="support-stat-late">
              <span className="support__stat-label">{t('support.stats.late')}</span>
              <span className="support__stat-value" data-tone={horsSla > 0 ? 'danger' : undefined}>
                {horsSla}
              </span>
            </p>
          </div>

          {lignes.length === 0 ? (
            <SystemState
              bodyKey="support.emptyBody"
              testId="support-empty"
              titleKey="support.emptyTitle"
            />
          ) : (
            <QueueShell
              columns={COLONNES}
              decisions={GESTES}
              entries={lignes}
              footer={
                <p className="support__count" data-testid="support-count">
                  {t('support.count', { shown: lignes.length })}
                </p>
              }
              onDecide={(_, decisionId) => {
                setGeste(decisionId as SupportGesture);
                setErreurGeste(null);
              }}
              onOpen={ouvrir}
              renderDetail={() => (
                <TicketPanel
                  champs={champsDuGeste}
                  dossier={dossier}
                  erreur={erreurDossier}
                  erreurGeste={erreurGeste}
                  geste={geste}
                  onSubmit={soumettreLeGeste}
                />
              )}
            />
          )}
        </>
      )}
    </section>
  );
}

/** Le dossier ouvert : la fiche, le fil, l'historique, les opérations. */
function TicketPanel({
  champs,
  dossier,
  erreur,
  erreurGeste,
  geste,
  onSubmit,
}: {
  readonly champs: readonly FormFieldDescriptor[];
  readonly dossier: SupportTicketFile | null;
  readonly erreur: string | null;
  readonly erreurGeste: string | null;
  readonly geste: SupportGesture | null;
  readonly onSubmit: (valeurs: Readonly<Record<string, string>>) => void | Promise<void>;
}): ReactNode {
  const { t } = useTranslation();

  if (erreur !== null) {
    return (
      <p className="support__notice" role="alert">
        {t(erreur)}
      </p>
    );
  }
  if (dossier === null) {
    return <p className="support__idle">{t('support.loading')}</p>;
  }

  return (
    <div className="support__file" data-testid="support-file">
      <QueueDetailSection title={t('support.detail.ticket')}>
        <QueueFact label={t('support.detail.status')}>{dossier.ticket.status}</QueueFact>
        <QueueFact label={t('support.detail.category')}>{dossier.ticket.categoryCode}</QueueFact>
        <QueueFact label={t('support.detail.priority')}>{String(dossier.ticket.priority)}</QueueFact>
        <QueueFact label={t('support.detail.channel')}>{dossier.ticket.channel}</QueueFact>
        <QueueFact label={t('support.detail.sla')}>{dossier.ticket.slaDueAt ?? t('support.detail.noSla')}</QueueFact>
        {/* Une escalade se DIT : elle est la preuve qu'une échéance a été
            manquée, et elle ne se lit nulle part ailleurs. */}
        <QueueFact label={t('support.detail.escalation')}>{
            dossier.ticket.escalatedAt === null
              ? t('support.detail.noEscalation')
              : t('support.detail.escalated', {
                  level: dossier.ticket.escalationLevel,
                  at: dossier.ticket.escalatedAt,
                })
          }</QueueFact>
      </QueueDetailSection>

      {/* **La fiche client peut manquer ENTIÈREMENT**, et c'est un fait : un
          ticket dont le client a été clôturé n'en a plus. Rendre une fiche vide
          se lirait comme un client sans solde. */}
      <QueueDetailSection title={t('support.detail.customer')}>
        {dossier.customer === null ? (
          <p className="support__idle" data-testid="support-no-customer">
            {t('support.detail.noCustomer')}
          </p>
        ) : (
          <>
            <QueueFact label={t('support.detail.ref')}>{dossier.customer.publicRef}</QueueFact>
            <QueueFact label={t('support.detail.customerStatus')}>{dossier.customer.status}</QueueFact>
            <QueueFact label={t('support.detail.balance')}>{
                dossier.customer.balanceMinorUnits === null
                  ? t('support.detail.noBalance')
                  : `${String(dossier.customer.balanceMinorUnits)} ${dossier.customer.currency ?? ''}`
              }</QueueFact>
          </>
        )}
      </QueueDetailSection>

      <QueueDetailSection title={t('support.detail.thread')}>
        {dossier.thread.length === 0 ? (
          <p className="support__idle">{t('support.detail.noThread')}</p>
        ) : (
          <ul className="support__thread">
            {dossier.thread.map((message) => (
              <li className="support__message" data-internal={message.internal} key={message.id}>
                {/* **Le MOT, et pas seulement la teinte.** Une note interne
                    prise pour une réponse ferait croire à un agent qu'il a
                    écrit au client ; une réponse prise pour une note ferait
                    partir au client des mots destinés à un collègue. */}
                <span className="support__message-kind">
                  {message.internal
                    ? t('support.detail.internalNote')
                    : t('support.detail.publicReply')}
                </span>
                <span className="support__message-meta">{message.postedAt}</span>
                <span className="support__message-body">{message.body}</span>
              </li>
            ))}
          </ul>
        )}
      </QueueDetailSection>

      <QueueDetailSection title={t('support.detail.history')}>
        <ul className="support__history">
          {dossier.history.map((evenement) => (
            <li className="support__event" key={evenement.id}>
              <span className="support__event-type">{evenement.eventType}</span>
              <span className="support__message-meta">{evenement.occurredAt}</span>
            </li>
          ))}
        </ul>
      </QueueDetailSection>

      <QueueDetailSection title={t('support.detail.operations')}>
        {dossier.transactions.length === 0 ? (
          <p className="support__idle" data-testid="support-no-operation">
            {t('support.detail.noOperation')}
          </p>
        ) : (
          <ul className="support__history">
            {dossier.transactions.map((operation) => (
              <li className="support__event" key={operation.publicRef}>
                <span className="support__event-type">{operation.publicRef}</span>
                <span className="support__message-meta">
                  {t('support.detail.operation', {
                    type: operation.txType,
                    amount: operation.amount,
                    status: operation.status,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </QueueDetailSection>

      {geste === null ? null : (
        <QueueDetailSection title={t('support.detail.gesture')}>
          {erreurGeste === null ? null : (
            <p className="support__notice" role="alert">
              {t(erreurGeste)}
            </p>
          )}
          <FormShell
            fields={champs}
            layout="stacked"
            onSubmit={onSubmit}
            submitLabelKey={`support.gestures.${geste.toLowerCase()}Submit`}
          />
        </QueueDetailSection>
      )}
    </div>
  );
}

/**
 * L'état du SLA d'un ticket — le mot, puis la teinte.
 *
 * L'information n'est **jamais portée par la couleur seule** : un opérateur qui
 * distingue mal le rouge du gris doit lire « dépassé » en toutes lettres.
 */
function SlaBadge({ ticket }: { readonly ticket: SupportTicket }): ReactNode {
  const { t } = useTranslation();
  if (ticket.slaDueAt === null) {
    return <span className="support__sla">{t('support.sla.none')}</span>;
  }
  const depasse = estDepasse(ticket);
  return (
    <span className="support__sla" data-tone={depasse ? 'danger' : 'success'}>
      {depasse ? t('support.sla.late') : t('support.sla.onTime')}
    </span>
  );
}

/**
 * L'échéance est-elle passée ?
 *
 * Comparaison d'un instant SERVI à maintenant, et rien de plus. L'écran ne
 * recalcule aucune échéance : voir l'en-tête du fichier.
 */
function estDepasse(ticket: SupportTicket): boolean {
  if (ticket.slaDueAt === null) return false;
  const echeance = Date.parse(ticket.slaDueAt);
  // Une date illisible n'est PAS un dépassement : annoncer « hors SLA » sur une
  // valeur qu'on n'a pas su lire serait une alarme fabriquée.
  if (Number.isNaN(echeance)) return false;
  return echeance < Date.now();
}

/** Un état de zone — voir `ReportingScreen` pour ce que trois `<p>` gris coûtent. */
function SystemState({
  bodyKey,
  testId,
  titleKey,
}: {
  readonly bodyKey: string;
  readonly testId: string;
  readonly titleKey: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="support__state" data-testid={testId}>
      <span aria-hidden="true" className="support__state-emblem" />
      <p className="support__state-title">{t(titleKey)}</p>
      <p className="support__state-body">{t(bodyKey)}</p>
    </div>
  );
}
